import { EHeatmapFilterType } from '~/modules/heatmap/types';
import { z } from 'zod';

export const HEATMAP_FILTER_ERROR_CODE = {
  MIN_GTE_MAX: 'MIN_GTE_MAX',
} as const;

export const HEATMAP_FILTER_ERROR_VALUE = {
  MIN_GTE_MAX: 'Min value must be less than max value',
} as const;

const campaignValueSchema = z
  .object({
    campaignId: z.string(),
    campaignGroupId: z.string().optional(),
    campaignVariantId: z.string(),
    campaignVariantType: z.string().min(1),
  })
  .refine((val) => !!val.campaignId || !!val.campaignGroupId);

const parseRangeValue = (str: string | undefined): number | undefined => {
  if (str === undefined || str === '') return undefined;
  const num = parseInt(str, 10);
  return isNaN(num) ? undefined : num;
};

const rangeFilterSchema = z
  .object({
    value: z.tuple([z.string(), z.string()]),
  })
  .superRefine((data, ctx) => {
    const min = parseRangeValue(data.value?.[0]);
    const max = parseRangeValue(data.value?.[1]);

    // At least one bound is required; allow entering only Min or only Max.
    if (min === undefined && max === undefined) {
      ctx.addIssue({ code: 'custom' });
      return;
    }

    // Ordering is only meaningful when both bounds are present.
    if (min !== undefined && max !== undefined && min >= max) {
      ctx.addIssue({
        code: 'custom',
        params: { errorCode: HEATMAP_FILTER_ERROR_CODE.MIN_GTE_MAX },
      });
    }
  });

// Both operator and value must be non-empty strings
const referralSourceSchema = z
  .object({
    operator: z.string().min(1).optional(),
    value: z.string().min(1).optional(),
  })
  .refine((data) => !!data.operator && !!data.value);

const campaignSchema = z.object({
  value: campaignValueSchema,
});

// Journey: each step's subFilter campaign data must be complete if present
const visitorJourneySchema = z.object({
  value: z
    .array(
      z
        .object({
          id: z.string(),
          path: z.string(),
          level: z.number(),
          isHeatmap: z.boolean(),
          subFilter: z
            .object({
              matchType: z.string().optional(),
              campaignData: campaignValueSchema.optional(),
            })
            .optional(),
        })
        .refine((step) => {
          // Non-heatmap steps must have a page selected
          if (!step.isHeatmap && !step.path) return false;

          // If subFilter is toggled on, it must have at least one type chosen
          if (step.subFilter && !step.subFilter.matchType && !step.subFilter.campaignData)
            return false;

          // If campaign sub-filter is selected, campaign data must be complete
          if (!step.subFilter?.campaignData) return true;

          return campaignValueSchema.safeParse(step.subFilter.campaignData).success;
        }),
    )
    .min(1),
});

const nonEmptyArraySchema = z.object({
  value: z.array(z.string()).min(1),
});

const nonEmptyValueSchema = z.object({
  value: z.string().min(1),
});

export const FILTER_SCHEMAS: Partial<Record<EHeatmapFilterType, z.ZodType>> = {
  [EHeatmapFilterType.ORDER_VALUE]: rangeFilterSchema,
  [EHeatmapFilterType.TIME_ON_PAGE]: rangeFilterSchema,
  [EHeatmapFilterType.REFERRAL_SOURCE]: referralSourceSchema,
  [EHeatmapFilterType.CAMPAIGN]: campaignSchema,
  [EHeatmapFilterType.VISITOR_JOURNEY]: visitorJourneySchema,
  [EHeatmapFilterType.VISITOR_TYPE]: nonEmptyArraySchema,
  [EHeatmapFilterType.TRAFFIC_SOURCE]: nonEmptyArraySchema,
  [EHeatmapFilterType.PURCHASE_TYPE]: nonEmptyValueSchema,
};
